import React, { useState } from "react";
import { CheckCircle, AlertCircle, X, Code, ShieldCheck, Copy, Check, Download, Music, ArrowDown } from "lucide-react";

const AUDIO_EXTENSIONS = [".mp3", ".wav", ".ogg", ".m4a", ".aac", ".flac", ".webm", ".mpeg"];

const isAudioUrl = (value, contentType) => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!/^https?:\/\//i.test(trimmed)) return false;
  if (typeof contentType === "string" && contentType.toLowerCase().startsWith("audio/")) {
    return true;
  }
  try {
    const pathname = new URL(trimmed).pathname.toLowerCase();
    return AUDIO_EXTENSIONS.some((ext) => pathname.endsWith(ext));
  } catch {
    return false;
  }
};

const getAudioFormat = (url, contentType) => {
  const type = (contentType || "").toLowerCase();
  let extension = "";
  try {
    extension = new URL(url).pathname.toLowerCase();
  } catch {
    extension = "";
  }
  if (extension.endsWith(".wav") || type.includes("wav")) return "WAV";
  if (extension.endsWith(".mp3") || extension.endsWith(".mpeg") || type.includes("mpeg") || type.includes("mp3")) return "MP3";
  if (extension.endsWith(".ogg") || type.includes("ogg")) return "OGG";
  if (extension.endsWith(".m4a") || type.includes("mp4")) return "M4A";
  if (extension.endsWith(".aac")) return "AAC";
  if (extension.endsWith(".flac")) return "FLAC";
  if (extension.endsWith(".webm") || type.includes("webm")) return "WEBM";
  return "AUDIO";
};

const AUDIO_CARD_STYLES = {
  input: {
    card: "border-[#D9F3E6] bg-[#F3FBF7]",
    icon: "bg-[#E3F6EC] text-[#1F8A5B]",
    badge: "bg-[#E3F6EC] text-[#1F8A5B]",
  },
  output: {
    card: "border-[#F6E7C4] bg-[#FFFBF4]",
    icon: "bg-[#FFF1D6] text-[#C48A1A]",
    badge: "bg-[#FFF1D6] text-[#C48A1A]",
  },
};

const AudioCard = ({ src, title, language, format, variant }) => {
  const [failed, setFailed] = useState(false);
  const styles = AUDIO_CARD_STYLES[variant];

  return (
    <div className={`rounded-2xl border px-4 py-4 ${styles.card}`}>
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${styles.icon}`}>
          <Music className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 leading-tight">{title}</p>
          {language && <p className="text-xs text-gray-500 mt-0.5">{language}</p>}
        </div>
        <span className={`text-[11px] font-semibold px-2 py-1 rounded-md ${styles.badge}`}>
          {format}
        </span>
        <a
          href={src}
          download
          target="_blank"
          rel="noreferrer"
          className="text-gray-400 hover:text-gray-600 p-1 shrink-0"
          aria-label={`Download ${title.toLowerCase()} audio`}
          title="Download audio"
        >
          <Download className="w-4 h-4" />
        </a>
      </div>
      <div className="mt-3 rounded-xl bg-[#F3F4F6] px-2 py-1.5">
        {failed ? (
          <p className="text-sm text-red-600 px-2 py-1.5">This audio couldn't be played.</p>
        ) : (
          <audio
            controls
            preload="metadata"
            src={src}
            className="block w-full"
            onError={() => setFailed(true)}
          />
        )}
      </div>
    </div>
  );
};

const LogDetailsModal = ({ show = false, selectedLog = null, onClose, loading = false }) => {
  const [copiedField, setCopiedField] = useState(null);

  if (!show || !selectedLog) return null;

  const handleCopy = (field, text) => {
    if (text === null || text === undefined) return;
    const content = typeof text === "object" ? JSON.stringify(text, null, 2) : String(text);
    navigator.clipboard.writeText(content);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const isSuccess = selectedLog.status?.toLowerCase() === "success";
  const tokens = selectedLog.tokensUsed ?? selectedLog.token ?? 0;
  const timeDisplay = selectedLog.createdAt
    ? new Date(selectedLog.createdAt).toLocaleString()
    : selectedLog.date
      ? new Date(selectedLog.date).toLocaleString()
      : selectedLog.when || "-";
  const logId = selectedLog._id || selectedLog.id || "-";
  const userId = selectedLog.userId || "-";
  const endpointDisplay = selectedLog.endpoint || selectedLog.service || "-";
  const statusCode = selectedLog.successDetails?.statusCode || selectedLog.errorDetails?.statusCode || selectedLog.statusCode;

  const sourceLang = selectedLog.sourceLang || selectedLog.successDetails?.requestData?.sourceLanguage || selectedLog.successDetails?.requestData?.sourceLang;
  const targetLang = selectedLog.targetLang || selectedLog.successDetails?.requestData?.targetLanguage || selectedLog.successDetails?.requestData?.targetLang;
  const outputContentType = selectedLog.successDetails?.responseHeaders?.["content-type"]
    || selectedLog.successDetails?.responseHeaders?.["Content-Type"];
  const requestContentType = selectedLog.successDetails?.requestHeaders?.["content-type"]
    || selectedLog.successDetails?.requestHeaders?.["Content-Type"];
  const inputText = typeof selectedLog.input === "string" ? selectedLog.input.trim() : "";
  const outputText = typeof selectedLog.output === "string" ? selectedLog.output.trim() : "";
  const inputIsSpeech = /speech-to-text|speech translation|\/stt\/|\/st\//i.test(
    `${selectedLog.type || ""} ${selectedLog.endpoint || ""}`
  );
  const inputAudioUrl = isAudioUrl(inputText, inputIsSpeech ? "audio/*" : requestContentType) ? inputText : "";
  const outputAudioUrl = isAudioUrl(outputText, outputContentType) ? outputText : "";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 mb-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-bold text-gray-900">
              Log Details
            </h3>
            {loading && (
              <span className="inline-flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">
                <div className="w-3 h-3 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                Fetching full details...
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg border border-gray-100">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Timestamp
              </label>
              <p className="text-sm text-gray-900 font-medium">
                {timeDisplay}
              </p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Status
              </label>
              <div className="flex items-center gap-2">
                {isSuccess ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-red-500" />
                )}
                <span
                  className={`text-sm font-bold ${isSuccess ? "text-green-600" : "text-red-600"
                    }`}
                >
                  {selectedLog.status ? selectedLog.status.toUpperCase() : (isSuccess ? "SUCCESS" : "ERROR")}
                </span>
                {statusCode && (
                  <span className="text-xs text-gray-500 font-mono">
                    (HTTP {statusCode})
                  </span>
                )}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Endpoint
              </label>
              <p className="text-sm text-gray-900 font-mono break-all">
                {endpointDisplay}
              </p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Response Time
              </label>
              <p className="text-sm text-gray-900 font-medium">
                {selectedLog.responseTime ? `${selectedLog.responseTime}ms` : "-"}
              </p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Tokens Used
              </label>
              <p className="text-sm text-gray-900 font-medium">
                {tokens.toLocaleString()}
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Log ID
                </label>
                {logId !== "-" && (
                  <button
                    onClick={() => handleCopy("logId", logId)}
                    className="text-xs text-gray-500 hover:text-green-600 flex items-center gap-1"
                    title="Copy Log ID"
                  >
                    {copiedField === "logId" ? (
                      <>
                        <Check className="w-3 h-3 text-green-600" />
                        <span className="text-green-600">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-900 font-mono truncate" title={logId}>
                {logId}
              </p>
            </div>
            {userId !== "-" && (
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    User ID
                  </label>
                  <button
                    onClick={() => handleCopy("userId", userId)}
                    className="text-xs text-gray-500 hover:text-green-600 flex items-center gap-1"
                    title="Copy User ID"
                  >
                    {copiedField === "userId" ? (
                      <>
                        <Check className="w-3 h-3 text-green-600" />
                        <span className="text-green-600">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>
                <p className="text-sm text-gray-900 font-mono truncate" title={userId}>
                  {userId}
                </p>
              </div>
            )}
          </div>

          {/* Translation Details */}
          {(selectedLog.input || selectedLog.output || sourceLang || targetLang) && (
            <div className="border-t pt-5">
              <h4 className="text-md font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Code className="w-4 h-4 text-gray-600" />
                API details
              </h4>
              {inputAudioUrl && (
                <p className="text-[11px] font-semibold tracking-[0.14em] text-gray-400 uppercase mb-3">
                  Audio
                </p>
              )}
              <div className="space-y-3">
                {inputAudioUrl ? (
                  <AudioCard
                    key={inputAudioUrl}
                    src={inputAudioUrl}
                    title="Input"
                    language={sourceLang}
                    format={getAudioFormat(inputAudioUrl, requestContentType)}
                    variant="input"
                  />
                ) : selectedLog.input ? (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-semibold text-gray-500 uppercase">
                        Input Text{sourceLang ? ` · ${sourceLang}` : ""}
                      </label>
                      <button
                        onClick={() => handleCopy("input", selectedLog.input)}
                        className="text-xs text-gray-500 hover:text-green-600 flex items-center gap-1"
                      >
                        {copiedField === "input" ? (
                          <>
                            <Check className="w-3 h-3 text-green-600" />
                            <span className="text-green-600">Copied</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            <span>Copy</span>
                          </>
                        )}
                      </button>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap font-sans">
                        {typeof selectedLog.input === "object"
                          ? JSON.stringify(selectedLog.input, null, 2)
                          : selectedLog.input}
                      </p>
                    </div>
                  </div>
                ) : null}
                {inputAudioUrl && outputAudioUrl && (
                  <div className="flex justify-center">
                    <span className="w-8 h-8 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center">
                      <ArrowDown className="w-4 h-4" />
                    </span>
                  </div>
                )}
                {outputAudioUrl ? (
                  <AudioCard
                    key={outputAudioUrl}
                    src={outputAudioUrl}
                    title="Output"
                    language={targetLang}
                    format={getAudioFormat(outputAudioUrl, outputContentType)}
                    variant="output"
                  />
                ) : selectedLog.output ? (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-semibold text-gray-500 uppercase">
                        Output Text{targetLang ? ` · ${targetLang}` : ""}
                      </label>
                      <button
                        onClick={() => handleCopy("output", selectedLog.output)}
                        className="text-xs text-gray-500 hover:text-green-600 flex items-center gap-1"
                      >
                        {copiedField === "output" ? (
                          <>
                            <Check className="w-3 h-3 text-green-600" />
                            <span className="text-green-600">Copied</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            <span>Copy</span>
                          </>
                        )}
                      </button>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap font-sans">
                        {typeof selectedLog.output === "object"
                          ? JSON.stringify(selectedLog.output, null, 2)
                          : selectedLog.output}
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {/* Success Details */}
          {selectedLog.successDetails && (
            <div className="border-t pt-5">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-bold text-green-700 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-green-600" />
                  Success Details
                </h4>
                {selectedLog.successDetails.statusCode && (
                  <span className="px-2.5 py-1 text-xs font-bold bg-green-100 text-green-800 border border-green-200 rounded-full">
                    HTTP {selectedLog.successDetails.statusCode}
                  </span>
                )}
              </div>

              <div className="space-y-4">

                {selectedLog.successDetails.requestData && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-semibold text-gray-500 uppercase">
                        Request Data
                      </label>
                      <button
                        onClick={() => handleCopy("requestData", selectedLog.successDetails.requestData)}
                        className="text-xs text-gray-500 hover:text-green-600 flex items-center gap-1"
                      >
                        {copiedField === "requestData" ? (
                          <>
                            <Check className="w-3 h-3 text-green-600" />
                            <span className="text-green-600">Copied</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            <span>Copy JSON</span>
                          </>
                        )}
                      </button>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-60 overflow-y-auto">
                      <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                        {typeof selectedLog.successDetails.requestData === "object"
                          ? JSON.stringify(selectedLog.successDetails.requestData, null, 2)
                          : selectedLog.successDetails.requestData}
                      </pre>
                    </div>
                  </div>
                )}

                {selectedLog.successDetails.response && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-semibold text-gray-500 uppercase">
                        Response Data
                      </label>
                      <button
                        onClick={() => handleCopy("response", selectedLog.successDetails.response)}
                        className="text-xs text-gray-500 hover:text-green-600 flex items-center gap-1"
                      >
                        {copiedField === "response" ? (
                          <>
                            <Check className="w-3 h-3 text-green-600" />
                            <span className="text-green-600">Copied</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            <span>Copy JSON</span>
                          </>
                        )}
                      </button>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-72 overflow-y-auto">
                      <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                        {typeof selectedLog.successDetails.response === "object"
                          ? JSON.stringify(selectedLog.successDetails.response, null, 2)
                          : selectedLog.successDetails.response}
                      </pre>
                    </div>
                  </div>
                )}

                {(selectedLog.successDetails.requestHeaders || selectedLog.successDetails.responseHeaders) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedLog.successDetails.requestHeaders && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs font-semibold text-gray-500 uppercase">
                            Request Headers
                          </label>
                          <button
                            onClick={() => handleCopy("requestHeaders", selectedLog.successDetails.requestHeaders)}
                            className="text-xs text-gray-500 hover:text-green-600 flex items-center gap-1"
                          >
                            {copiedField === "requestHeaders" ? (
                              <>
                                <Check className="w-3 h-3 text-green-600" />
                                <span className="text-green-600">Copied</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3" />
                                <span>Copy</span>
                              </>
                            )}
                          </button>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                          <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                            {JSON.stringify(selectedLog.successDetails.requestHeaders, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                    {selectedLog.successDetails.responseHeaders && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs font-semibold text-gray-500 uppercase">
                            Response Headers
                          </label>
                          <button
                            onClick={() => handleCopy("responseHeaders", selectedLog.successDetails.responseHeaders)}
                            className="text-xs text-gray-500 hover:text-green-600 flex items-center gap-1"
                          >
                            {copiedField === "responseHeaders" ? (
                              <>
                                <Check className="w-3 h-3 text-green-600" />
                                <span className="text-green-600">Copied</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3" />
                                <span>Copy</span>
                              </>
                            )}
                          </button>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                          <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                            {JSON.stringify(selectedLog.successDetails.responseHeaders, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error Details */}
          {selectedLog.errorDetails && (
            <div className="border-t pt-5">
              <h4 className="text-md font-bold text-red-600 mb-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-500" />
                Error Details
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                    Error Message
                  </label>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800 font-medium">
                      {selectedLog.errorDetails.message}
                    </p>
                  </div>
                </div>
                {selectedLog.errorDetails.statusCode && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                      Status Code
                    </label>
                    <p className="text-sm text-gray-900 font-mono font-bold">
                      HTTP {selectedLog.errorDetails.statusCode}
                    </p>
                  </div>
                )}
                {selectedLog.errorDetails.stack && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                      Stack Trace
                    </label>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                      <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                        {selectedLog.errorDetails.stack}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Raw Log Data */}
          <div className="border-t pt-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-md font-bold text-gray-900">
                Raw Log Data
              </h4>
              <button
                onClick={() => handleCopy("rawLog", selectedLog)}
                className="text-xs text-gray-500 hover:text-green-600 flex items-center gap-1"
              >
                {copiedField === "rawLog" ? (
                  <>
                    <Check className="w-3 h-3 text-green-600" />
                    <span className="text-green-600">Copied JSON</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    <span>Copy JSON</span>
                  </>
                )}
              </button>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-64 overflow-y-auto">
              <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                {JSON.stringify(selectedLog, null, 2)}
              </pre>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-gray-600 text-white text-sm font-semibold rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogDetailsModal;
